// Creates a new message object
export const createMessage = (text, sender, options = {}) => {
    const message = {
        id: Date.now() + Math.random(), // Simple unique ID generation
        text,
        sender,
        timestamp: new Date(),
        ...options
    };

    return message;
};

// Creates a bot message with loading state
export const createLoadingMessage = (text) => {
    return createMessage(text, 'bot', { isLoading: true });
};

// Creates a bot message with options
export const createMessageWithOptions = (text, options = []) => {
    return createMessage(text, 'bot', { options });
};

// Creates a message with workspace options
export const createWorkspaceOptionsMessage = (text, workspaceOptions) => {
    return createMessage(text, 'bot', {
        workspaceOptions,
        showWorkspaceOptions: true
    });
};

// Creates a message with location options
export const createLocationOptionsMessage = (text, locationOptions, rateOptions, availableSpaces, selectedWorkspace) => {
    return createMessage(text, 'bot', {
        dynamicLocationOptions: locationOptions,
        dynamicRateOptions: rateOptions,
        availableSpaces,
        selectedWorkspace
    });
};

// Creates a message with price range options
export const createPriceRangeMessage = (text, priceRanges, filteredSpaces) => {
    return createMessage(text, 'bot', {
        priceRanges,
        filteredSpaces
    });
};

// Creates a message with recommended spaces
export const createRecommendedSpacesMessage = (text, recommendedSpaces) => {
    return createMessage(text, 'bot', {
        recommendedSpaces
    });
};

// Creates a message with follow-up options
export const createFollowUpOptionsMessage = (text) => {
    return createMessage(text, 'bot', {
        showFollowUpOptions: true
    });
};

// Creates a message with "find something else" option
export const createFindSomethingElseMessage = (text) => {
    return createMessage(text, 'bot', {
        showFindSomethingElse: true
    });
};

// Creates a message with previous booking question
export const createPreviousBookingMessage = (text, previousEventType) => {
    return createMessage(text, 'bot', {
        previousEventType,
        showPreviousBookingOptions: true
    });
};

// Generates personalized welcome message for logged-in users
export const createPersonalizedWelcomeMessage = (userName, bookingCount, previousEventType = null, previousLocation = 'Colombo') => {
    if (previousEventType) {
        const text = `Hi ${userName},\n\nYou've booked ${bookingCount} time${bookingCount > 1 ? 's' : ''} with MillionSpaces! We're excited to have you back.\n\nYour previous room was a ${previousEventType} in ${previousLocation}. Do you want to book the same space?`;
        return createMessage(text, 'bot', {
            previousEventType,
            showPreviousBookingOptions: true
        });
    } else if (bookingCount === 0) {
        const text = `Hi ${userName || 'there'},\n\nWe're happy to have you back on MillionSpaces. Let us find a great space for you today.`;
        return createMessage(text, 'bot');
    } else {
        const text = `Hi ${userName},\n\nYou've booked ${bookingCount} time${bookingCount > 1 ? 's' : ''} with MillionSpaces! We're excited to have you back.\nWhat are you looking to book today?`;
        return createMessage(text, 'bot');
    }
};

// Generates default welcome message for non-logged in users 
export const createDefaultWelcomeMessage = () => {
    const text = "Hi there,\n\nI am Spacey, your smart assistant ready to help you find the perfect space.";
    return createMessage(text, 'bot');
};

// Generates fallback welcome message
export const createFallbackWelcomeMessage = () => {
    const text = "Hi there!\n\nWelcome to MillionSpaces, your trusted booking platform to discover meeting rooms, workspaces, virtual offices & photoshoot spaces across Sri Lanka.\n\nI'm Spacey, your smart assistant. Ready to help you find the perfect space!";
    return createMessage(text, 'bot');
};

// Generates secondary welcome message
export const createSecondaryWelcomeMessage = () => {
    const text = "Hi I'm Spacey, What are you looking for today?";
    return createMessage(text, 'bot');
};

// Checks if message contains specific keywords
export const messageContainsKeywords = (message, keywords) => {
    if (!message || !keywords || !Array.isArray(keywords)) return false;

    const lowerMessage = message.toLowerCase();
    return keywords.some(keyword => lowerMessage.includes(keyword.toLowerCase()));
};

// Checks if user is asking about booking process
export const isBookingProcessQuestion = (message) => {
    const bookingProcessKeywords = [
        'how i can proceed',
        'how can i proceed',
        'how to proceed',
        'proceed with booking',
        'how do i proceed',
        'what are the ways',
        'ways to book',
        'how to proceed a booking',
        'how should i proceed',
        'what should i do to book',
        'how do i go about',
        'what are my options',
        'how can i get started',
        'how do i start booking'
    ];

    return messageContainsKeywords(message, bookingProcessKeywords);
};

// Checks if user is asking about how to book
export const isHowToBookQuestion = (message) => {
    const howToBookKeywords = [
        'how to book',
        'booking process',
        'tell me how to book',
        'steps to book',
        'booking steps'
    ];

    return messageContainsKeywords(message, howToBookKeywords);
};

// Checks if user is asking to book something
export const isBookingRequest = (message) => {
    const bookingKeywords = [
        'book',
        'booked',
        'booking'
    ];

    return messageContainsKeywords(message, bookingKeywords);
};

// Checks if user is asking about workspace options
export const isWorkspaceOptionsQuestion = (message) => {
    const workspaceOptionsKeywords = [
        'options',
        'workspace types',
        'workspace options',
        'what are the other',
        'types available',
        'show me all',
        'list all',
        'what types',
        'what kinds'
    ];

    // More specific check to avoid false positives
    const lowerMessage = message.toLowerCase();

    // Only trigger for explicit requests about workspace types
    if (lowerMessage.includes('available workspace') && lowerMessage.includes('type')) {
        return true;
    }

    // Check for other specific keywords
    return workspaceOptionsKeywords.some(keyword => lowerMessage.includes(keyword));
};

// Generates workspace options list text
export const generateWorkspaceOptionsText = (workspaceOptions, selectedWorkspace = null) => {
    if (!workspaceOptions || !Array.isArray(workspaceOptions)) {
        return "No workspace options available.";
    }

    const workspaceList = workspaceOptions.map(ws => {
        let description = '';
        if (ws.description) {
            description = ` - ${ws.description}`;
        }
        return `🏢 **${ws.label}**${description}`;
    }).join('\n\n');

    const currentSelection = selectedWorkspace ?
        `\n\n✅ **Currently Selected:** ${selectedWorkspace.label}` :
        '\n\nℹ️ **No workspace type selected yet**';

    return `Here are all available workspace types:\n\n${workspaceList}${currentSelection}`;
};

// Generates booking process explanation text
export const generateBookingProcessText = () => {
    return "There are a few ways to do that. First, you can select a workspace type from the categories above, manually browse to find the right workspace, and book it. Alternatively, you can use the chat option and follow the guided stepper to get a recommendation and book it. Or, you can contact our agent and ask them to book a space for you.";
};

// Generates booking steps explanation text
export const generateBookingStepsText = () => {
    return "Tell first select his preferred workspace type, then choose the location and budget. Based on that, Spacey will suggest suitable spaces. Click on a suggested item to view the details, then click 'Continue' to select the space.";
};

// Generates booking restriction message
export const generateBookingRestrictionMessage = () => {
    return "Sorry, I cannot book a workspace for you. Please select an option, view the details, and click 'Continue' to book.";
};

// Generates workspace type restriction message
export const generateWorkspaceTypeRestrictionMessage = (selectedWorkspaceType) => {
    return `Information is only available for the currently selected workspace type: ${selectedWorkspaceType.label}. To inquire about a different type, please select it from the options above.`;
};

// Generates contact agent message
export const generateContactAgentMessage = () => {
    return "I'll connect you with a human agent. Please fill out the contact form below.";
};

// Generates help message
export const generateHelpMessage = () => {
    return "Here's some helpful information about Spacey! Check out the info panel below or type.";
};

// Generates error message
export const generateErrorMessage = () => {
    return 'Sorry, I could not process your request. You can type "agent" to speak with a human agent.';
};

// Generates success message for contact form
export const generateContactFormSuccessMessage = () => {
    return "Thank you! Your message has been sent. We'll get back to you soon.";
};

// Generates error message for contact form
export const generateContactFormErrorMessage = () => {
    return "Sorry, there was an error sending your message. Please try again or contact us directly.";
};

// Generates form validation error message
export const generateFormValidationErrorMessage = () => {
    return "Please check your form inputs and try again.";
};
